import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Form, message } from 'antd';
import { useProjectCRUD } from 'hooks/useProject/useProjectCRUD';
import { ClientOptionsModel, ProjectModel, ProjectType, UpdateProjectModel } from 'core/domain/project/model/projectModel';
import { GetClientsList } from 'core/domain/client/repositories/getClientsList';
import {
  ProjectContextualMenuTranslations,
  ProjectEditFormData,
  ProjectEditFormTranslations,
  ProjectEditFormValues,
  ProjectEditModalTranslations,
  ProjectHeaderDescription,
} from 'components/pages/ProjectPage/resources/models';
import { getClientPathById } from 'components/pages/App/routes/settings/config';
import { checkEmailListErrors } from 'utils/email';
import { useProfileUser } from 'hooks/useProfileUser';
import { UserRoleType } from 'models/users.model';
import { getDeleteProjectErrorText, projectTypeTranslationManager } from 'utils/projects';
import { DeleteProject } from 'core/domain/project/repositories/deleteProject';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { MessageErrorModel, useMessage } from 'hooks/useMessage';
import { DELAY_MESSAGE_TIME } from 'constants/time';
import { getProjectsWithPaginationPath } from 'components/pages/App/routes/projects/config';
import { TABLE_PAGINATION_PAGE_DEFAULT, TABLE_PAGINATION_SIZE_DEFAULT } from 'constants/table';

const checkIsDataEmpty = (data: ProjectModel) => {
  return Object.keys(data).length === 0;
};

export const useProject = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { projectId } = useParams<{ projectId: string }>();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const { data: userData } = useProfileUser();
  const { setMessageError, setMessageSuccess } = useMessage();
  const { data, loading, updateProject, updating, getProjectById } = useProjectCRUD();
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [projectType, setProjectType] = useState<ProjectType>(ProjectType.HOME);
  const [hasLoadedClients, setHasLoadedClients] = useState<boolean>(false);
  const [isClientSelectLoading, setIsClientSelectLoading] = useState<boolean>(false);
  const [isClientSelectOpen, setIsClientSelectOpen] = useState<boolean>(false);
  const [clientOptions, setClientOptions] = useState<ClientOptionsModel[]>([]);
  const [form] = Form.useForm<ProjectEditFormValues>();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const isUserSuperAdmin = !!userData?.roles.includes(UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN);

  const contextualMenuTranslations: ProjectContextualMenuTranslations = {
    editButton: t('edit'),
    deleteButton: t('delete'),
    notifyButton: t('notify_the_community'),
  };

  const editModalTranslations: ProjectEditModalTranslations = {
    title: t('_EDIT_PROJECT_MODAL_TITLE'),
    cancelButton: t('to_cancel'),
    editButton: t('edit'),
  };

  const editFormTranslations: ProjectEditFormTranslations = {
    name: t('_EDIT_PROJECT_FORM_NAME'),
    namePlaceholder: t('_EDIT_PROJECT_FORM_NAME_PLACEHOLDER'),
    nameError: t('_EDIT_PROJECT_FORM_NAME_ERROR'),
    supportEmails: t('_EDIT_PROJECT_FORM_SUPPORT_EMAILS'),
    supportEmailsPlaceholder: t('_EDIT_PROJECT_FORM_SUPPORT_EMAILS_PLACEHOLDER'),
    emailErrorFormat: t('_EDIT_PROJECT_FORM_EMAIL_ERROR_FORMAT'),
    emailFormat: t('_EDIT_PROJECT_FORM_EMAIL_FORMAT'),
    usersVerifiers: t('_EDIT_PROJECT_FORM_USERS_VERIFIERS'),
    usersVerifiersPlaceholder: t('_EDIT_PROJECT_FORM_USERS_VERIFIERS_PLACEHOLDER'),
    usersRequireVerification: t('_EDIT_PROJECT_FORM_USERS_REQUIRE_VERIFICATION'),
    fieldRequired: t('field_required'),
    submitError: t('_EDIT_PROJECT_FORM_SUBMIT_ERROR'),
    submitSuccess: t('_EDIT_PROJECT_FORM_SUBMIT_SUCCESS'),
    type: t('_EDIT_PROJECT_FORM_TYPE'),
    client: t('_EDIT_PROJECT_FORM_CLIENT'),
    clientPlaceholder: t('_EDIT_PROJECT_FORM_CLIENT_PLACEHOLDER'),
  };

  const headerDescriptionTranslations: ProjectHeaderDescription = {
    name: t('name'),
    supportEmail: t('support_email'),
    verifiers: t('registration_validator_user'),
    client: t('company'),
    clientId: null,
    requireVerification: t('_ENABLED_REQUIRE_VERIFICATION'),
    requireVerificationInfo: t('registration_validator_user_info'),
    type: t('_PROJECT_TYPE'),
  };

  const deleteProjectModalTranslations = {
    title: t('_DELETE_PROJECT_MODAL_TITLE'),
    text: t('_DELETE_PROJECT_MODAL_TEXT'),
    mainButtonText: t('delete'),
    secondaryButtonText: t('cancel'),
  };

  const getInitialEditFormValues = (project: ProjectModel) => {
    const formattedSupportEmails = project.supportEmail;
    const formattedUsersVerifiers = project.usersVerifiers?.map(({ value }) => value);
    const formattedClient = project.client?.id && project.client?.name ? { value: project.client.id, label: project.client.name } : null;

    setProjectType(project.type as unknown as ProjectType);

    form.setFieldsValue({
      name: project.name,
      usersRequireVerification: project.usersRequireVerification,
      usersVerifiers: formattedUsersVerifiers,
      supportEmails: formattedSupportEmails,
      type: projectTypeTranslationManager[project.type as unknown as ProjectType],
      client: formattedClient,
    });
  };

  const onOpenEditModal = () => setIsEditModalVisible(true);

  const onCloseEditModal = () => {
    setIsEditModalVisible(false);
    !!data && getInitialEditFormValues(data);
  };

  const formatUsersVerifiersData = (users: string[]): string[] => {
    return users
      .flatMap((user) => data.allowedUsersToVerifyAccounts.filter((allowedUser) => allowedUser.value === user) || [])
      .map((user) => user.id);
  };

  const getFormattedEditFormData = (values: ProjectEditFormValues): UpdateProjectModel => {
    const { name, usersRequireVerification, usersVerifiers, supportEmails, type, client } = values;
    const formattedUsersVerifiers = formatUsersVerifiersData(usersVerifiers);
    const formattedClientId = client?.value ?? null;

    return {
      ...data,
      name,
      usersVerifiers: formattedUsersVerifiers,
      usersRequireVerification,
      supportEmail: supportEmails,
      type,
      clientId: formattedClientId,
    };
  };

  const onSubmitEditForm = async (values: ProjectEditFormValues) => {
    const formattedFormData = getFormattedEditFormData({ ...values, type: projectType });
    try {
      await updateProject(formattedFormData);
      getProjectById(projectId);
    } finally {
      setIsEditModalVisible(false);
    }
  };

  const onSubmitEditFormFailed = () => {
    message.error(editFormTranslations.submitError, 3);
  };

  useEffect(() => {
    const isDataEmpty = checkIsDataEmpty(data);
    !isDataEmpty && getInitialEditFormValues(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const getTypeOptions = () => {
    return Object.values(ProjectType).map((option) => {
      return { id: option, value: projectTypeTranslationManager[option] };
    });
  };

  const onSelectType = (value: ProjectType) => {
    setProjectType(value);
  };

  const onClickClientLink = (clientId: string | null) => {
    if (!!clientId) {
      const route = getClientPathById({ clientId });
      history.push(route);
    }
  };

  const getClients = async () => {
    setIsClientSelectLoading(true);
    try {
      const data = await GetClientsList();
      const clients = data?.map(({ id, name }) => ({
        label: name,
        value: id,
      }));

      if (clients) {
        setClientOptions(clients);
        setHasLoadedClients(true);
        setIsClientSelectOpen(true);
      }
    } catch {
      setMessageError({ description: t('_EDIT_PROJECT_FORM_CLIENT_ERROR') });
    } finally {
      setIsClientSelectLoading(false);
    }
  };

  const onClientSelectVisibleChange = (open: boolean) => {
    if (open && !hasLoadedClients) {
      setIsClientSelectOpen(false);
      getClients();
      return;
    }

    setIsClientSelectOpen(open);
  };

  const formData: ProjectEditFormData = {
    form,
    editFormTranslations,
    typeOptions: getTypeOptions(),
    clientOptions,
    onSubmitEditForm,
    onSubmitEditFormFailed,
    onSelectType,
    validateEmailFormat: checkEmailListErrors,
    onClientSelectVisibleChange,
    isClientSelectLoading,
    isClientSelectOpen,
    editing: updating,
  };

  const getHeaderDescription = (data: ProjectModel): ProjectHeaderDescription => {
    const isDataEmpty = checkIsDataEmpty(data);

    if (isDataEmpty) {
      return {
        name: t('not_reported'),
        supportEmail: t('_EDIT_PROJECT_SUPPORT_USERS_EMPTY'),
        verifiers: t('_EDIT_PROJECT_USERS_VERIFIERS_EMPTY'),
        client: t('not_reported'),
        clientId: t('not_reported'),
        requireVerification: t('_DISABLED'),
        requireVerificationInfo: t('registration_validator_user_info'),
        type: t('not_reported'),
      };
    }

    return {
      name: data.name,
      supportEmail: !!data.supportEmail.length ? data.supportEmail.join(', ') : t('_EDIT_PROJECT_SUPPORT_USERS_EMPTY'),
      verifiers: !!data.usersVerifiers.length
        ? data.usersVerifiers?.map((user) => user.value).join(', ')
        : t('_EDIT_PROJECT_USERS_VERIFIERS_EMPTY'),
      client: data.client.name,
      clientId: data.client.id,
      requireVerification: data.usersRequireVerification ? t('_ENABLED') : t('_DISABLED'),
      requireVerificationInfo: t('registration_validator_user_info'),
      type: projectTypeTranslationManager[data.type as unknown as ProjectType],
    };
  };

  const onOpenDeleteProjectModal = () => {
    setIsDeleteModalVisible(true);
  };

  const onCloseDeleteProjectModal = () => {
    setIsDeleteModalVisible(false);
  };

  const onDeleteProject = async () => {
    try {
      setIsDeleting(true);
      await DeleteProject({ host, token, projectId });
      setMessageSuccess({ description: t('_PROJECTS_LIST_DELETE_PROJECT_FORM_SUCCESS_MESSAGE') });
      onCloseDeleteProjectModal();
      history.length > 1
        ? history.goBack()
        : history.push(getProjectsWithPaginationPath({ page: TABLE_PAGINATION_PAGE_DEFAULT, size: TABLE_PAGINATION_SIZE_DEFAULT }));
    } catch (error) {
      const description = getDeleteProjectErrorText(error as MessageErrorModel);
      setMessageError({ description }, DELAY_MESSAGE_TIME.MEDIUM);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    data,
    loadingData: loading,
    contextualMenuTranslations,
    deleteProjectModalTranslations,
    modal: {
      editModalTranslations,
      onOpenEditModal,
      onCloseEditModal,
      editModalVisible: isEditModalVisible,
    },
    formData,
    header: {
      projectHeaderDescription: getHeaderDescription(data),
      headerDescriptionTranslations,
    },
    onClickClientLink,
    onOpenDeleteProjectModal,
    onCloseDeleteProjectModal,
    onDeleteProject,
    isUserSuperAdmin,
    isDeleteModalVisible,
    isDeleting,
  };
};
